import React, { useState, useEffect, useMemo } from 'react';
import { useAreas } from '../../hooks/useAreas';
import { formatCurrencyInt } from '../../utils/formatters';

// Columnas del tablero, de izquierda a derecha. area 'ambas' = compartida;
// pct_mesa es la parte del costo que va a Mesa y el resto va a Sitio.
const COLUMNAS = [
  { id: 'mesa_servicio', label: 'Mesa de Servicio' },
  { id: 'ambas', label: 'Compartidas' },
  { id: 'soporte_sitio', label: 'Soporte en Sitio' }
];
const fmtPct = (n) => String(Math.round(n * 100) / 100);

/**
 * Reparto editable de una herramienta compartida: se escribe el % de Mesa y
 * el de Sitio se completa solo (suman 100). Persiste al salir del campo.
 */
const RepartoInput = ({ herramienta, onSave }) => {
  const [val, setVal] = useState(fmtPct(herramienta.pct_mesa));

  useEffect(() => {
    setVal(fmtPct(herramienta.pct_mesa));
  }, [herramienta.pct_mesa]);

  const num = parseFloat(val);
  const valido = num > 0 && num < 100;

  const commit = () => {
    if (!valido) {
      setVal(fmtPct(herramienta.pct_mesa));
      return;
    }
    if (num !== herramienta.pct_mesa) onSave(num);
  };

  return (
    <div
      className={`areas-reparto ${valido ? '' : 'invalido'}`}
      title={valido
        ? `Mesa ${formatCurrencyInt(herramienta.costo * num / 100)} · Sitio ${formatCurrencyInt(herramienta.costo * (100 - num) / 100)}`
        : 'Debe estar entre 1 y 99'}
    >
      <span className="areas-reparto-lbl is-mesa">Mesa</span>
      <input
        className="areas-reparto-input"
        type="text"
        inputMode="decimal"
        value={val}
        aria-label={`Porcentaje de ${herramienta.name} para Mesa de Servicio`}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setVal(e.target.value.replace(/[^\d.]/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      <span className="areas-reparto-pct">%</span>
      <span className="areas-reparto-lbl is-sitio">
        Sitio {valido ? fmtPct(100 - num) : '—'}%
      </span>
    </div>
  );
};

/**
 * Junta en una fila las herramientas que comparten la primera palabra del
 * nombre (Cognos A/B/C → "Cognos", Maximo Aval/Crystal → "Maximo"): se
 * muestran con el costo sumado y el área se les aplica a todas a la vez.
 */
const agrupar = (herramientas) => {
  const primera = (h) => h.name.trim().split(/\s+/)[0].toLowerCase();
  const veces = {};
  herramientas.forEach((h) => { veces[primera(h)] = (veces[primera(h)] || 0) + 1; });
  const filas = [];
  const porGrupo = {};
  herramientas.forEach((h) => {
    const k = primera(h);
    if (veces[k] < 2) {
      filas.push({ key: `${h.catalogo}:${h.id}`, name: h.name, miembros: [h] });
      return;
    }
    if (!porGrupo[k]) {
      porGrupo[k] = { key: `grupo:${k}`, name: h.name.trim().split(/\s+/)[0], miembros: [] };
      filas.push(porGrupo[k]);
    }
    porGrupo[k].miembros.push(h);
  });
  // El área y el reparto del grupo son los de su primera herramienta (se
  // asignan siempre juntas, así que coinciden).
  return filas.map((f) => ({
    ...f,
    costo: f.miembros.reduce((s, m) => s + m.costo, 0),
    area: f.miembros[0].area,
    pct_mesa: f.miembros[0].pct_mesa
  }));
};

/**
 * Módulo Áreas (2026-10). Tablero de tres columnas: Mesa de Servicio a la
 * izquierda, Soporte en Sitio a la derecha y en el centro las compartidas,
 * cada una con su % para Mesa (el resto va a Sitio). Las herramientas se
 * mueven arrastrándolas o con las flechas; las sin asignar esperan arriba.
 */
export const AreasModule = ({ showNotification }) => {
  const { herramientas, loading, loadHerramientas, setArea } = useAreas(showNotification);
  const [arrastrando, setArrastrando] = useState(null);
  const [sobre, setSobre] = useState(null);

  useEffect(() => {
    loadHerramientas();
  }, [loadHerramientas]);

  const filas = useMemo(() => agrupar(herramientas), [herramientas]);
  const sinArea = filas.filter((f) => !f.area);
  const porColumna = (id) => filas.filter((f) => f.area === id);

  // Lo que le toca a cada área: sus herramientas completas más su parte
  // de las compartidas.
  const totalArea = (id) =>
    filas.reduce((s, f) => {
      if (f.area === id) return s + f.costo;
      if (f.area === 'ambas') {
        return s + f.costo * (id === 'mesa_servicio' ? f.pct_mesa : 100 - f.pct_mesa) / 100;
      }
      return s;
    }, 0);

  const mover = async (f, destino) => {
    if (f.area === destino) return;
    if (await setArea(f.miembros, destino, undefined, f.name)) {
      const col = COLUMNAS.find((c) => c.id === destino);
      showNotification?.(
        'success',
        'Áreas',
        destino ? `"${f.name}" → ${col.label}` : `"${f.name}" quedó sin asignar`
      );
    }
  };

  // --- Drag & drop ---
  const onDragStart = (e, f) => {
    setArrastrando(f.key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', f.key);
  };
  const onDragEnd = () => {
    setArrastrando(null);
    setSobre(null);
  };
  // destino null = Sin asignar; se resalta con la clave 'sin'.
  const zona = (id) => {
    const clave = id || 'sin';
    return {
      onDragOver: (e) => {
        if (!arrastrando) return;
        e.preventDefault();
        if (sobre !== clave) setSobre(clave);
      },
      onDragLeave: (e) => {
        if (!e.currentTarget.contains(e.relatedTarget) && sobre === clave) setSobre(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        const f = filas.find((x) => x.key === arrastrando);
        onDragEnd();
        if (f) mover(f, id);
      }
    };
  };

  // Se llama como función (no como <Componente/>) para que React no la
  // desmonte en cada render y el campo de % no pierda el foco.
  const tarjeta = (f, col) => {
    const idx = COLUMNAS.findIndex((c) => c.id === col);
    const izq = col === null ? 'mesa_servicio' : COLUMNAS[idx - 1]?.id;
    const der = col === null ? 'soporte_sitio' : COLUMNAS[idx + 1]?.id;
    return (
      <div
        key={f.key}
        className={`areas-tarjeta ${arrastrando === f.key ? 'dragging' : ''}`}
        draggable
        onDragStart={(e) => onDragStart(e, f)}
        onDragEnd={onDragEnd}
      >
        <div className="areas-tarjeta-fila">
          {izq ? (
            <button
              type="button"
              className="areas-flecha"
              title={`Mover a ${COLUMNAS.find((c) => c.id === izq).label}`}
              onClick={() => mover(f, izq)}
            >
              ‹
            </button>
          ) : <span className="areas-flecha-vacia" />}
          <span
            className="areas-nombre"
            title={f.miembros.length > 1 ? f.miembros.map((m) => m.name).join(' · ') : undefined}
          >
            {f.name}
          </span>
          <span className="areas-costo">{formatCurrencyInt(f.costo)}</span>
          {der ? (
            <button
              type="button"
              className="areas-flecha"
              title={`Mover a ${COLUMNAS.find((c) => c.id === der).label}`}
              onClick={() => mover(f, der)}
            >
              ›
            </button>
          ) : <span className="areas-flecha-vacia" />}
        </div>
        {col === 'ambas' && (
          <RepartoInput
            herramienta={f}
            onSave={(pct) => setArea(f.miembros, 'ambas', pct, f.name)}
          />
        )}
        {col !== null && (
          <button
            type="button"
            className="areas-quitar"
            title="Quitar área (vuelve a Sin asignar)"
            onClick={() => mover(f, null)}
          >
            ×
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Áreas</h1>
        <p>Arrastra cada herramienta a Mesa de Servicio, a Soporte en Sitio o al centro si es compartida</p>
      </div>

      {loading && filas.length === 0 && <p className="proceso-hint">Cargando…</p>}

      {sinArea.length > 0 && (
        <section
          className={`areas-pendientes ${sobre === 'sin' ? 'drop-over' : ''}`}
          {...zona(null)}
        >
          <span className="areas-pendientes-titulo">Sin asignar · {sinArea.length}</span>
          <div className="areas-pendientes-lista">
            {sinArea.map((f) => tarjeta(f, null))}
          </div>
        </section>
      )}

      <div className="areas-tablero">
        {COLUMNAS.map((c) => {
          const items = porColumna(c.id);
          const total = c.id === 'ambas'
            ? items.reduce((s, f) => s + f.costo, 0)
            : totalArea(c.id);
          return (
            <section
              key={c.id}
              className={`areas-columna areas-columna-${c.id} ${sobre === c.id ? 'drop-over' : ''}`}
              {...zona(c.id)}
            >
              <header className="areas-columna-header">
                <span className="areas-columna-titulo">{c.label}</span>
                <span className="proceso-col-count">{items.length}</span>
              </header>
              <div className="areas-columna-total">
                {formatCurrencyInt(total)} / mes
                {c.id !== 'ambas' && porColumna('ambas').length > 0 && (
                  <span className="areas-columna-nota">incluye su parte de las compartidas</span>
                )}
              </div>
              <div className="areas-columna-body">
                {items.length === 0 && <div className="areas-vacio">Suelta aquí</div>}
                {items.map((f) => tarjeta(f, c.id))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};
